import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Timer as TimerIcon, Target, Headphones, Keyboard, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LeadCaptureModal } from "@/components/AccessGates";
import { PARTS } from "@/data/parts";
import { getBank, getHistory } from "@/lib/bank";
import { PASS_SCORE } from "@/lib/scoring";
import { WeakSpotCard } from "@/components/WeakSpot";
import { StreakCard } from "@/components/StreakCard";

const Stat = ({ label, value, id }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-800/50 p-5">
    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">{label}</div>
    <div data-testid={id} className="mt-1 font-display text-3xl font-bold text-white">{value}</div>
  </div>
);

export default function Home() {
  const bank = getBank();
  const total = Object.values(bank).reduce((s, a) => s + a.length, 0);
  const hist = getHistory();
  const best = hist.length ? Math.max(...hist.map((h) => h.overall)) : "—";
  const { canDemo, hasAccess } = useAuth();
  const nav = useNavigate();
  const [lead, setLead] = useState(false);
  const startDemo = () => (canDemo ? nav("/practice") : setLead(true));

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8 sm:py-20">
      <section className="grid gap-12 lg:grid-cols-[1.4fr_1fr] lg:items-end">
        <div className="animate-rise space-y-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-indigo-500/40 bg-indigo-500/10 px-3 py-1 font-mono text-xs uppercase tracking-widest text-indigo-300"><Target className="h-3.5 w-3.5" />Pass target {PASS_SCORE}+</span>
          <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">The complete Versant English Placement Test, rehearsed under real conditions.</h1>
          <p className="max-w-xl text-base text-slate-400 md:text-lg">Nine official parts, strict timers, one-time audio and a calibrated 20–80 score report. Train in the Practice Arena, then prove it in 13 full mock exams from Easy to Hard.</p>
          <div className="flex flex-wrap gap-3 pt-2">
            {!hasAccess && <button onClick={startDemo} data-testid="home-start-demo-btn" className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 font-semibold text-slate-950 transition-colors hover:bg-emerald-400"><Sparkles className="h-4 w-4" />Start Free Demo</button>}
            <Link to={hasAccess ? "/mock" : "/pricing"} data-testid="home-start-mock-btn" className="group inline-flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-indigo-500">{hasAccess ? "Full Mock Exam Suite" : "Unlock all mock exams"}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
            {hasAccess && <Link to="/practice" data-testid="home-start-practice-btn" className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-6 py-3 font-semibold text-slate-200 transition-colors hover:bg-slate-800">Targeted Practice</Link>}
          </div>
          <LeadCaptureModal open={lead} onOpenChange={setLead} onDone={() => nav("/practice")} />
        </div>
        <div className="grid grid-cols-2 gap-3 animate-rise [animation-delay:120ms]">
          <Stat id="home-stat-bank" label="Questions loaded" value={total} />
          <Stat id="home-stat-mocks" label="Mock exams" value="12" />
          <Stat id="home-stat-taken" label="Exams taken" value={hist.length} />
          <Stat id="home-stat-best" label="Best score" value={best} />
        </div>
      </section>

      <section className="mt-20 space-y-6">
        <h2 className="text-base font-semibold uppercase tracking-[0.2em] text-slate-400 md:text-lg">The 9-part format</h2>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-9">
          {PARTS.map((p, i) => (
            <Link to={`/practice?part=${p.key}`} key={p.key} data-testid={`home-part-${p.key}`} className="animate-rise block cursor-pointer rounded-xl bg-white p-4 shadow-xl shadow-slate-950/30 ring-indigo-500 transition-transform hover:-translate-y-1 hover:ring-2 focus:outline-none focus-visible:ring-2" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="font-mono text-xs text-indigo-600">PART {p.key}</div>
              <div className="mt-1 font-display text-sm font-bold leading-tight text-slate-900">{p.name}</div>
              <div className="mt-3 flex items-center gap-1 font-mono text-[11px] text-slate-500"><TimerIcon className="h-3 w-3" />{p.time >= 60 ? `${p.time / 60}m` : `${p.time}s`} · {p.exam}×</div>
              <div className="mt-1 font-mono text-[11px] text-emerald-600">{bank[p.key].length} items</div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-20 grid gap-4 md:grid-cols-3">
        <div className="md:col-span-3"><StreakCard /></div>
        <div className="md:col-span-3"><WeakSpotCard /></div>
        {[[Headphones, "Zero-replay audio", "Exam audio plays once at a natural 0.95× pace. No pause, no replay."], [Keyboard, "Keyboard first", "Inputs auto-focus. Press Enter to submit single-line answers."], [Target, "Calibrated report", "20–80 scaled score with Listening, Reading, Speaking and Writing sub-scores."]].map(([I, t, d]) => (
          <div key={t} className="rounded-xl border border-slate-800 p-6"><I className="h-5 w-5 text-indigo-400" /><h3 className="mt-3 font-display font-bold text-white">{t}</h3><p className="mt-1 text-sm text-slate-400">{d}</p></div>
        ))}
      </section>
    </div>
  );
}
